import React, { useEffect, useState } from 'react';
import { Popover, Typography, Button, Box, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

interface AISearchDemoProps {
  children: React.ReactNode;
}

export const AISearchDemo: React.FC<AISearchDemoProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hasSeenDemo, setHasSeenDemo] = useState(true); // default to true to avoid flashing

  useEffect(() => {
    const seen = localStorage.getItem('hasSeenAISearchDemo');
    if (!seen) {
      setHasSeenDemo(false);
    }
  }, []);

  const handleGotIt = () => {
    localStorage.setItem('hasSeenAISearchDemo', 'true');
    setHasSeenDemo(true);
    setAnchorEl(null);
  };

  // Attach a ref to the child so we can anchor the popover
  const childRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasSeenDemo) {
      const id = window.setTimeout(() => {
        const el = childRef.current;
        if (el) setAnchorEl(el);
      }, 500);
      return () => window.clearTimeout(id);
    }
  }, [hasSeenDemo]);

  const open = Boolean(anchorEl) && !hasSeenDemo;

  return (
    <>
      <Box ref={childRef} display="inline-block">
        {children}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableAutoFocus
        disableEnforceFocus
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            maxWidth: 320,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1e1e24 0%, #2a2a35 100%)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" mb={1} color="#4fc3f7">
            <AutoAwesomeIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Try AI Search
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleGotIt} sx={{ color: 'rgba(255,255,255,0.7)', mt: -0.5, mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
          You can now search using natural language! Try typing <strong>"jazzy songs"</strong>, <strong>"sad piano"</strong>, or <strong>"chill lofi beats"</strong>.
        </Typography>
        <Box display="flex" justifyContent="flex-end">
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleGotIt}
            sx={{
              background: '#4fc3f7',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': {
                background: '#29b6f6'
              }
            }}
          >
            Got it!
          </Button>
        </Box>
      </Popover>
    </>
  );
};
